import Cookies from 'js-cookie'
import { v4 as uuid } from 'uuid'

import { ActionTypes } from '../state/ActionTypes'
import { notificationsSlice } from '../state/slices/notificationsSlice'
import type {
  ErrorNotificationAction,
  InfoNotificationAction,
  SuccessNotificationAction,
} from '../action-creators/NotificationActionCreators'
import {
  Notification,
  NotificationScopes,
  NotificationTypes,
} from '../values/Notification'

const CLOSED_NOTIFICATIONS_COOKIE_KEY = 'mc-closed-notifications'

type NotificationState = ReturnType<typeof notificationsSlice.getInitialState>
type NotificationScope = NotificationScopes | `${NotificationScopes}-${string}`

const getClosedNotifications = (): string[] => {
  const raw = Cookies.get(CLOSED_NOTIFICATIONS_COOKIE_KEY)
  return raw ? JSON.parse(raw) : []
}
const setClosedNotifications = (notifs: string[]): string | undefined =>
  Cookies.set(CLOSED_NOTIFICATIONS_COOKIE_KEY, JSON.stringify(notifs), {
    expires: 365 * 4,
  })

const getRemovedNotificationAndIndex = (
  id: string,
  _state: NotificationState,
): {
  removedNotificationIndex: number
  removedNotification: Notification | undefined
} => {
  const removedNotificationIndex = _state.visibleNotifications.findIndex(
    (notif) => notif.id === id,
  )
  const removedNotification =
    _state.visibleNotifications[removedNotificationIndex]
  return { removedNotificationIndex, removedNotification }
}

const isNotificationAlreadyInCookie = (
  removedNotification: Notification,
): boolean => {
  const closedNotifications = getClosedNotifications()
  const index = closedNotifications.indexOf(removedNotification.scope)
  // check if notification is already added to cookies
  return index !== -1
}

type RemoveNotificationOptions = {
  comesFromDirectUserAction?: boolean
  isHardCloseCheckboxChecked?: boolean
  shouldCloseVisibleNotification?: boolean
}

export type NotificationAction =
  | ({
      actionType: typeof ActionTypes.QUEUE_NOTIFICATION
      type: NotificationTypes.ERROR
    } & ErrorNotificationAction)
  | ({
      actionType: typeof ActionTypes.QUEUE_NOTIFICATION
      type: NotificationTypes.INFO
    } & InfoNotificationAction)
  | ({
      actionType: typeof ActionTypes.QUEUE_NOTIFICATION
      type: NotificationTypes.SUCCESS
    } & SuccessNotificationAction)
  | {
      actionType: typeof ActionTypes.REMOVE_NOTIFICATION
      id: string
      data?: RemoveNotificationOptions
    }
  | {
      actionType: typeof ActionTypes.REMOVE_NOTIFICATION_COOKIE
      id: string
    }
  | {
      actionType: typeof ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE
      scope: NotificationScopes
      comesFromDirectUserAction: boolean
    }
  | {
      actionType: typeof ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE_PREFIX
      scopePrefix: string
      comesFromDirectUserAction: boolean
    }
  | {
      actionType: typeof ActionTypes.REMOVE_COMPOSER_NOTICES
      draftId: string
    }
  | { actionType: typeof ActionTypes.APP_RESET }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every((item) => typeof item === 'string')

const isNotificationScope = (value: unknown): value is NotificationScope =>
  typeof value === 'string' &&
  (Object.values(NotificationScopes).some((scope) => scope === value) ||
    Object.values(NotificationScopes).some((scope) =>
      value.startsWith(`${scope}-`),
    ))

const isNotificationType = (value: unknown): value is NotificationTypes =>
  Object.values(NotificationTypes).some((type) => type === value)

const isRemoveNotificationOptions = (
  value: unknown,
): value is RemoveNotificationOptions =>
  value === undefined ||
  (isRecord(value) &&
    (value.comesFromDirectUserAction === undefined ||
      typeof value.comesFromDirectUserAction === 'boolean') &&
    (value.isHardCloseCheckboxChecked === undefined ||
      typeof value.isHardCloseCheckboxChecked === 'boolean') &&
    (value.shouldCloseVisibleNotification === undefined ||
      typeof value.shouldCloseVisibleNotification === 'boolean'))

const isNotificationCta = (
  value: unknown,
): value is NonNullable<InfoNotificationAction['cta']> =>
  isRecord(value) &&
  typeof value.label === 'string' &&
  typeof value.isUpgradePath === 'boolean' &&
  typeof value.action === 'function'

export const isNotificationAction = (
  action: unknown,
): action is NotificationAction => {
  if (!isRecord(action) || typeof action.actionType !== 'string') return false

  switch (action.actionType) {
    case ActionTypes.QUEUE_NOTIFICATION: {
      const hasValidQueueData =
        isNotificationType(action.type) &&
        typeof action.message === 'string' &&
        (action.errorCode === undefined ||
          typeof action.errorCode === 'number') &&
        (action.onlyCloseOnce === undefined ||
          typeof action.onlyCloseOnce === 'boolean') &&
        (action.showSoftAndHardCloseOptions === undefined ||
          typeof action.showSoftAndHardCloseOptions === 'boolean') &&
        (action.isUnique === undefined ||
          typeof action.isUnique === 'boolean') &&
        (action.data === undefined || isStringRecord(action.data)) &&
        (action.cta === undefined || isNotificationCta(action.cta))

      if (!hasValidQueueData) return false
      if (action.type === NotificationTypes.INFO) {
        return isNotificationScope(action.scope)
      }
      return action.scope === undefined || isNotificationScope(action.scope)
    }
    case ActionTypes.REMOVE_NOTIFICATION:
      return (
        typeof action.id === 'string' &&
        isRemoveNotificationOptions(action.data)
      )
    case ActionTypes.REMOVE_NOTIFICATION_COOKIE:
      return typeof action.id === 'string'
    case ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE:
      return (
        isNotificationScope(action.scope) &&
        typeof action.comesFromDirectUserAction === 'boolean'
      )
    case ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE_PREFIX:
      return (
        typeof action.scopePrefix === 'string' &&
        typeof action.comesFromDirectUserAction === 'boolean'
      )
    case ActionTypes.REMOVE_COMPOSER_NOTICES:
      return typeof action.draftId === 'string'
    case ActionTypes.APP_RESET:
      return true
    default:
      return false
  }
}

const removeNotification = (
  id: string,
  {
    comesFromDirectUserAction = true,
    isHardCloseCheckboxChecked = false,
    shouldCloseVisibleNotification = true,
  }: RemoveNotificationOptions = {},
  _state: NotificationState,
): void => {
  const { removedNotification, removedNotificationIndex } =
    getRemovedNotificationAndIndex(id, _state)
  /**
   * Sometimes `removeNotification()` is called right after the app state is reset
   * and `state.visibleNotifications` is empty. This check ensures we don't try
   * to remove a non-existent notification.
   */
  if (!removedNotification) {
    return
  }

  if (shouldCloseVisibleNotification) {
    _state.visibleNotifications.splice(removedNotificationIndex, 1)
  }

  const rememberClosedNotification =
    removedNotification.onlyCloseOnce &&
    (!removedNotification.showSoftAndHardCloseOptions ||
      (removedNotification.showSoftAndHardCloseOptions &&
        isHardCloseCheckboxChecked)) &&
    comesFromDirectUserAction &&
    !isNotificationAlreadyInCookie(removedNotification)

  if (rememberClosedNotification) {
    const closedNotifications = getClosedNotifications()
    setClosedNotifications([...closedNotifications, removedNotification.scope])
  }
}

const removeAllNotificationsByScope = (
  scope: NotificationScope,
  comesFromDirectUserAction: boolean,
  _state: NotificationState,
): void => {
  const notifications = _state.visibleNotifications.filter(
    (notif) => notif.scope === scope,
  )
  notifications.forEach((notif) =>
    removeNotification(notif.id, { comesFromDirectUserAction }, _state),
  )
}

const removeAllNotificationsByScopePrefix = (
  scopePrefix: string,
  comesFromDirectUserAction: boolean,
  _state: NotificationState,
): void => {
  const notifications = _state.visibleNotifications.filter((notif) =>
    notif.scope?.startsWith(scopePrefix),
  )
  notifications.forEach((notif) =>
    removeNotification(notif.id, { comesFromDirectUserAction }, _state),
  )
}

const removeNotificationCookie = (
  id: string,
  _state: NotificationState,
): void => {
  const { removedNotification } = getRemovedNotificationAndIndex(id, _state)
  if (!removedNotification) {
    return
  }
  const closedNotifications = getClosedNotifications()
  const index = closedNotifications.indexOf(removedNotification.scope)
  if (isNotificationAlreadyInCookie(removedNotification)) {
    closedNotifications.splice(index, 1)
    setClosedNotifications(closedNotifications)
  }
}
const removeComposerOmniboxNotices = (
  draftId: string,
  _state: NotificationState,
): void => {
  const notifications = _state.visibleNotifications.filter(
    (notif) =>
      notif.data?.id === draftId &&
      notif.scope === NotificationScopes.MC_OMNIBOX_EDIT_NOTICE,
  )
  notifications.forEach((notif) => removeNotification(notif.id, {}, _state))
}
const shouldShowNotification = (notification: Notification): boolean => {
  if (!notification.onlyCloseOnce) return true

  const closedNotifications = getClosedNotifications()
  const wasAlreadyClosed = closedNotifications.includes(notification.scope)

  return !wasAlreadyClosed
}

export const notificationReducer = (
  draft: NotificationState,
  action: NotificationAction,
): NotificationState | undefined => {
  switch (action.actionType) {
    case ActionTypes.QUEUE_NOTIFICATION:
      {
        const { isUnique, ...actionData } = action
        // Remove prev notifications that share the same scope when a scope is identified as unique

        if (isUnique)
          removeAllNotificationsByScope(actionData.scope, false, draft)

        const id = uuid()
        let notification

        switch (actionData.type) {
          case NotificationTypes.ERROR:
            notification = Notification.getNewErrorNotification(id, actionData)
            break

          case NotificationTypes.SUCCESS:
            notification = Notification.getNewSuccessNotification(
              id,
              actionData,
            )
            break

          case NotificationTypes.INFO:
            notification = Notification.getNewInfoNotification(id, actionData)
            break

          default:
            return
        }

        if (shouldShowNotification(notification))
          draft.visibleNotifications.push(notification)
      }
      break

    case ActionTypes.REMOVE_NOTIFICATION:
      removeNotification(action.id, action.data, draft)
      break

    case ActionTypes.REMOVE_NOTIFICATION_COOKIE:
      removeNotificationCookie(action.id, draft)
      break

    case ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE:
      removeAllNotificationsByScope(
        action.scope,
        action.comesFromDirectUserAction,
        draft,
      )
      break

    case ActionTypes.REMOVE_ALL_NOTIFICATIONS_BY_SCOPE_PREFIX:
      removeAllNotificationsByScopePrefix(
        action.scopePrefix,
        action.comesFromDirectUserAction,
        draft,
      )
      break

    case ActionTypes.REMOVE_COMPOSER_NOTICES:
      removeComposerOmniboxNotices(action.draftId, draft)
      break

    case ActionTypes.APP_RESET:
      // we need to return the new state here because
      // doing `draft = notificationsSlice.getInitialState()` would replace the
      // object reference and won't work
      return notificationsSlice.getInitialState()
    default:
  }
}
